import { makeAssistantToolUI } from '@assistant-ui/react'
import { Loader2, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// What became of the proposal rides the tool message's artifact as
// `{ outcome }` (see emit_schema_proposal in
// src/libs/agents/spectre/agents/schema_builder/tools.py); the prose is for the model.
const OUTCOME_COPY: Record<string, string> = {
  accepted: 'schemaAgent.workbench.toolComplete',
  rejected: 'schemaAgent.workbench.toolFailed',
  recorded_with_errors: 'schemaAgent.workbench.toolRecordedWithErrors',
}

function reportedOutcome(artifact: unknown): string | undefined {
  if (typeof artifact !== 'object' || artifact === null || !('outcome' in artifact))
    return undefined
  return typeof artifact.outcome === 'string' ? artifact.outcome : undefined
}

/** The artifact answers once the tool has; before that the call is still
 * running, was cut off, or predates the artifact (older transcripts). */
function outcomeCopyKey(status: { type: string }, artifact: unknown, isError?: boolean): string {
  if (isError) return 'schemaAgent.workbench.toolFailed'
  const copy = OUTCOME_COPY[reportedOutcome(artifact) ?? '']
  if (copy) return copy
  if (status.type === 'running' || status.type === 'requires-action')
    return 'schemaAgent.workbench.toolPreparing'
  return status.type === 'incomplete'
    ? 'schemaAgent.workbench.toolInterrupted'
    : 'schemaAgent.workbench.toolFinished'
}

interface SchemaProposalToolCardProps {
  proposal: { name?: string; rationale?: string }
  result: { status: { type: string }; artifact?: unknown; isError?: boolean }
}

export function SchemaProposalToolCard({ proposal, result }: SchemaProposalToolCardProps) {
  const { t } = useTranslation()
  const copyKey = outcomeCopyKey(result.status, result.artifact, result.isError)
  const preparing = copyKey === 'schemaAgent.workbench.toolPreparing'
  return (
    <div className="my-2 rounded-lg border bg-muted/30 p-3 text-sm">
      <p className="flex items-center gap-2 font-medium">
        {preparing ? (
          <Loader2 className="size-4 shrink-0 motion-safe:animate-spin text-primary" aria-hidden />
        ) : (
          <Sparkles className="size-4 shrink-0 text-primary" aria-hidden />
        )}
        {proposal.name ?? t('schemaAgent.workbench.pending')}
      </p>
      <p role={preparing ? 'status' : undefined} className="mt-1 text-muted-foreground">
        {t(copyKey)}
      </p>
      {proposal.rationale && (
        <details className="mt-2 text-xs">
          <summary className="cursor-pointer text-muted-foreground">
            {t('schemaAgent.workbench.reasoning')}
          </summary>
          <p className="mt-2 leading-relaxed">{proposal.rationale}</p>
        </details>
      )}
    </div>
  )
}

export const SchemaProposalToolUI = makeAssistantToolUI<
  SchemaProposalToolCardProps['proposal'],
  string
>({
  toolName: 'emit_schema_proposal',
  render: ({ args, artifact, status, isError }) => (
    <SchemaProposalToolCard proposal={args} result={{ artifact, status, isError }} />
  ),
})
